import constants from "../../constants";
import { ORDER_TOKEN_HEADER } from "../../lib/orderViewToken";
import { tunnelFetch } from "../api/tunnelFetch";
import { getPublicServerEndpoint, isLocalNetwork } from "../helper";

const TURNSTILE_TOKEN_HEADER = "X-Turnstile-Token";

const storefrontDomain = () => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;

  if (isLocalNetwork(host)) {
    return `${constants.devEnv.storeSubdomain}.sh0p.in`;
  }

  return host;
};

const storefrontHeaders = () => ({
  Accept: "application/json",
  "X-Store-Domain": storefrontDomain(),
});

const storefrontUrl = (path, query) => {
  const base = `${getPublicServerEndpoint()}/storefront${path}`;

  if (!query) return base;
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

const parseStorefrontError = async (res) => {
  let body = null;

  try {
    body = await res.json();
  } catch {
    // ignore
  }

  const err = new Error(body?.error?.message || `Storefront request failed (${res.status})`);
  err.status = res.status;

  err.code = body?.error?.code;
  err.body = body;

  return err;
};

export const getStorefrontSession = async () => {
  const res = await tunnelFetch(storefrontUrl("/session"), { headers: storefrontHeaders() });
  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

export const getStorefrontLegal = async () => {
  const res = await tunnelFetch(storefrontUrl("/legal"), { headers: storefrontHeaders() });
  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

export const getStorefrontCollections = async () => {
  const res = await tunnelFetch(storefrontUrl("/collections"), { headers: storefrontHeaders() });
  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

/** `{ collection: { id, title, handle }, redirect }` -- `redirect` is the canonical handle for an old URL. */
export const getStorefrontCollection = async (handle) => {
  const res = await tunnelFetch(storefrontUrl(`/collections/${encodeURIComponent(handle)}`), {
    headers: storefrontHeaders(),
  });
  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

/**
 * `collection` is a collection handle, or "all". `filters` are lib/listingFilters query params
 * (minPrice, maxPrice, inStock, sort); the response also carries `priceRange`.
 */
export const getStorefrontProducts = async ({
  page = 1,
  limit = 20,
  collection = "all",
  q,
  filters,
  signal,
} = {}) => {
  const handle = collection && collection !== "all" ? collection : "all";
  const path = `/collections/${encodeURIComponent(handle)}/products`;

  const res = await tunnelFetch(storefrontUrl(path, { page, limit, q, ...filters }), {
    headers: storefrontHeaders(),
    signal,
  });

  if (!res.ok) throw await parseStorefrontError(res);
  return res.json();
};

export const getStorefrontProductById = async (productId) => {
  const res = await tunnelFetch(storefrontUrl(`/products/${productId}`), {
    headers: storefrontHeaders(),
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

export const getStorefrontHome = async ({ page = 1, limit = 5, productLimit = 4 } = {}) => {
  const res = await tunnelFetch(storefrontUrl("/home", { page, limit, productLimit }), {
    headers: storefrontHeaders(),
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

export const getStorefrontCheckout = async ({ cartTotal } = {}) => {
  const res = await tunnelFetch(storefrontUrl("/checkout", { cartTotal }), {
    headers: storefrontHeaders(),
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

export const postStorefrontCheckoutPincode = async ({ pincode }) => {
  const res = await tunnelFetch(storefrontUrl("/checkout/pincode"), {
    method: "POST",
    headers: {
      ...storefrontHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ pincode }),
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

export const postStorefrontCheckoutDiscount = async ({ code, cartTotal }) => {
  const res = await tunnelFetch(storefrontUrl("/checkout/discount"), {
    method: "POST",
    headers: {
      ...storefrontHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ code, cartTotal }),
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

/** `turnstileToken` (hooks/useTurnstile) is required by the API once Turnstile is configured. */
export const postStorefrontCheckoutOrder = async (body, { turnstileToken } = {}) => {
  const res = await tunnelFetch(storefrontUrl("/checkout/order"), {
    method: "POST",
    headers: {
      ...storefrontHeaders(),
      "Content-Type": "application/json",
      ...(turnstileToken ? { [TURNSTILE_TOKEN_HEADER]: turnstileToken } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

/** `viewToken` (lib/orderViewToken) unlocks the buyer's contact details and address. */
export const getStorefrontOrderById = async (orderId, viewToken) => {
  const res = await tunnelFetch(storefrontUrl(`/orders/${encodeURIComponent(orderId)}`), {
    headers: {
      ...storefrontHeaders(),
      ...(viewToken ? { [ORDER_TOKEN_HEADER]: viewToken } : {}),
    },
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};

/** Razorpay Checkout options for a pending order (the custom-domain /pay page). Needs the view token. */
export const getStorefrontOrderPayment = async (orderId, viewToken) => {
  const res = await tunnelFetch(storefrontUrl(`/orders/${encodeURIComponent(orderId)}/payment`), {
    headers: {
      ...storefrontHeaders(),
      ...(viewToken ? { [ORDER_TOKEN_HEADER]: viewToken } : {}),
    },
  });

  if (!res.ok) throw await parseStorefrontError(res);

  return res.json();
};
